import { Button, Checkbox, Grid } from "antd";
import classNames from "classnames";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdError } from "react-icons/md";
import { SuccessBannerIcon } from "src/assets/Icons";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import ImageWithEmptyFallback from "src/components/ImageWithEmptyFallback/ImageWithEmptyFallback";
import { ExchangeInfoType, ProgramType } from "src/Modules/PointsExchange/constants";
import { formatNumber } from "src/utility";
import InstructionCards from "../InstructionCards/InstructionCards";

export type ExchangeModalContainerProps = {
  destination: ProgramType;
  exchangeInfo: ExchangeInfoType;
  termsAndConditions?: string;
};

export type ExchangeFormProps = {
  destination: ProgramType;
  points: number;
  pointsAfter: number;
  isLoading: boolean;
  onSubmit: () => void;
  onCancel: () => void;
};

export type ExchangeResultProps = {
  error: any;
  data: any;
  termsAndConditions?: string;
  onBack: () => void;
  onClose: () => void;
};

export const ExchangeTriggerButton: FC<{ onOpen: () => void }> = ({ onOpen }) => {
  const { t } = useTranslation();
  const [accepted, setAccepted] = useState(false);

  return (
    <div className="flex w-full flex-col gap-3">
      <Checkbox checked={accepted} onChange={(e) => setAccepted(e.target.checked)}>
        <span className="text-xs text-font-subtle">
          {t("PointsExchange.exchange.acceptTermsPrefix")}{" "}
          <span className="text-font-active font-semibold">{t("PointsExchange.exchange.termsAndConditions")}</span>
        </span>
      </Checkbox>
      <Button
        size="large"
        block
        disabled={!accepted}
        onClick={onOpen}
        className={classNames("font-semibold! p-6!", accepted ? "bg-background-primary! text-font-inverted!" : "bg-background-disabled! text-font-disabled!")}
      >
        {t("PointsExchange.exchange.exchangeNow")}
      </Button>
    </div>
  );
};

export const ExchangeForm: FC<ExchangeFormProps> = ({ destination, points, pointsAfter, isLoading, onSubmit, onCancel }) => {
  const { t } = useTranslation();
  return (
    <div className="flex w-full flex-col gap-4 p-4 py-6">
      <div className="flex items-center gap-3">
        <ImageWithEmptyFallback
          src={destination?.imageUrl}
          alt={destination?.name ?? ""}
          className="h-14 w-14 min-w-14 max-w-25 p-1 object-contain outline outline-border-default rounded-sm"
        />
        <div className="flex flex-col">
          <div className="text-font-minimal text-sm">{t("PointsExchange.exchange.transferredTo")}</div>
          <div className="text-font-default text-lg font-semibold">{destination?.name}</div>
        </div>
      </div>
      <div className="divide-border-minimal flex w-full flex-col divide-y">
        <div className="flex w-full items-center justify-between py-2">
          <div className="text-font-danger text-sm font-semibold">{t("PointsExchange.exchange.pointsDeducted")}</div>
          <div className="text-font-danger text-base font-bold">
            {t("PointsExchange.exchange.pointsDeductedValue", { points: formatNumber(points) })}
          </div>
        </div>
        <div className="flex w-full items-center justify-between py-2">
          <div className="text-font-subtle text-sm">{t("PointsExchange.exchange.unitReceived", { unit: destination?.unitLabel })}</div>
          <div className="text-font-active text-base font-bold">
            {formatNumber(pointsAfter)} {destination?.unitLabel}
          </div>
        </div>
      </div>
      <div className="flex w-full flex-col gap-2">
        <Button
          type="primary"
          size="large"
          block
          className="bg-background-primary! font-semibold! p-5!"
          loading={isLoading}
          iconPlacement="end"
          onClick={onSubmit}
        >
          {t("PointsExchange.exchange.confirmExchange")}
        </Button>
        <Button size="large" block onClick={onCancel}>
          {t("PointsExchange.exchange.goBack")}
        </Button>
      </div>
    </div>
  );
};

export const ExchangeResultStep: FC<ExchangeResultProps> = ({ error, termsAndConditions, onBack, onClose }) => {
  const { t } = useTranslation();
  const isLargeScreen = !!Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];
  const isError = !!error;

  return (
    <div className="flex w-full flex-col items-center gap-6 p-6 py-10 text-center">
      <div className={classNames("flex h-24 w-24 items-center justify-center rounded-full", isError ? "bg-background-danger" : "bg-background-minimal")}>
        {isError ? <MdError className="text-font-danger h-14 w-14" /> : <SuccessBannerIcon className="text-font-success h-14 w-14" />}
      </div>
      <div className="flex flex-col gap-1">
        <div className={classNames("text-xl font-semibold", isError ? "text-font-danger" : "text-font-default")}>
          {isError ? t("PointsExchange.exchange.exchangeFailed") : t("PointsExchange.exchange.exchangeComplete")}
        </div>
        <div className="text-font-subtle text-sm">
          {isError ? (error?.data?.message ?? t("PointsExchange.exchange.genericError")) : t("PointsExchange.exchange.exchangeSuccessMessage")}
        </div>
      </div>
      {!isError && !isLargeScreen && <InstructionCards termsAndConditions={termsAndConditions} title={t("PointsExchange.exchange.guideTitle")} />}
      <div className="flex w-full flex-col gap-2">
        <Button type="primary" size="large" block className="bg-background-primary! font-semibold!" onClick={onBack}>
          {isError ? t("PointsExchange.exchange.tryAgain") : t("PointsExchange.exchange.exchangeMorePoints")}
        </Button>
        <Button size="large" block onClick={onClose}>
          {isError ? t("PointsExchange.exchange.cancel") : t("PointsExchange.exchange.goHome")}
        </Button>
      </div>
    </div>
  );
};
